import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Landing from "./Landing";
import { Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";

// Claim links land here: the full CoachConnect front page with a sticky
// "accept your profile" banner on top. Loading this page soft-claims the
// profile (click-through = claimed by default); the strip at the bottom
// lets the coach release it and opt out of all outreach.
export default function ClaimLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [declining, setDeclining] = useState(false);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/claim/${token}`)
      .then(({ data }) => setInvite(data))
      .catch(() => setInvite({ status: "invalid" }));
  }, [token]);

  const name = invite?.coach?.name;
  const valid = invite && invite.status === "pending" && !declined;
  const softClaimed = valid && invite.soft_claimed;

  const handleDecline = async () => {
    if (!window.confirm("Release your profile and stop all CoachConnect emails? You can always sign up again later.")) return;
    setDeclining(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/claim/${token}/decline`);
      setDeclined(true);
    } catch {
      /* already finalized — treat as done */
      setDeclined(true);
    }
    setDeclining(false);
  };

  return (
    <div className="min-h-screen bg-appbg">
      {valid && (
        <div className="sticky top-0 z-50 bg-blaze text-white px-4 py-3" data-testid="claim-landing-banner">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2 min-w-0">
              {softClaimed ? <CheckCircle2 size={18} className="shrink-0 mt-0.5" /> : <Sparkles size={18} className="shrink-0 mt-0.5" />}
              <div className="text-sm leading-snug">
                {softClaimed ? (
                  <>
                    <span className="font-bold heading tracking-wide">{name ? `${name} — your profile is claimed.` : "Your profile is claimed."}</span>{" "}
                    We've reserved it for you. Finish setup (2 minutes) to unlock matching, alerts and your CIR score
                    . Free, always.
                  </>
                ) : (
                  <>
                    <span className="font-bold heading tracking-wide">{name ? `${name} — you've been scouted.` : "You've been scouted."}</span>{" "}
                    Your coaching profile is pre-built and ready to claim
                    . Free, always.
                  </>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate(`/claim/${token}/accept`)}
              data-testid="claim-banner-accept-btn"
              className="w-full sm:w-auto shrink-0 min-h-[46px] px-5 bg-white text-black font-extrabold heading text-xs tracking-widest hover:bg-neutral-200 transition-colors flex items-center justify-center gap-2"
            >
              {softClaimed ? "FINISH SETUP" : "ACCEPT MY PROFILE"} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}
      {invite?.status === "invalid" && (
        <div className="sticky top-0 z-50 bg-[#1a1a1f] border-b-2 border-blaze px-4 py-4" data-testid="claim-invalid-note">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-neutral-200 leading-snug">
              <span className="font-bold heading tracking-wide text-blaze">THIS CLAIM LINK ISN'T VALID ANYMORE.</span>{" "}
              It may have expired or been superseded — but your pre-built profile is most likely still waiting.
              Search for it below and we'll send a fresh claim link to your inbox.
            </div>
            <button
              onClick={() => navigate("/coaches/find")}
              data-testid="claim-invalid-find-btn"
              className="w-full sm:w-auto shrink-0 min-h-[46px] px-5 bg-blaze text-white font-extrabold heading text-xs tracking-widest hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              FIND MY PROFILE <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}
      {(declined || invite?.status === "declined") && (
        <div className="bg-neutral-900 text-neutral-300 text-sm px-4 py-3 text-center" data-testid="claim-declined-note">
          Done — your profile has been released and you won't hear from us again. Changed your mind? You can always create a free profile below.
        </div>
      )}
      <Landing claimCoach={valid ? invite.coach : null} />
      {valid && (
        <div className="border-t border-neutral-800 bg-appbg px-4 py-6" data-testid="claim-optout-strip">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 flex-wrap text-sm text-neutral-500">
            <span>
              Not you, or don't want a CoachConnect profile? No hard feelings — one click releases it and stops all emails.
            </span>
            <button
              onClick={handleDecline}
              disabled={declining}
              data-testid="claim-optout-btn"
              className="shrink-0 min-h-[36px] px-4 border border-neutral-700 text-neutral-400 heading text-[10px] tracking-widest hover:border-blaze hover:text-blaze transition-colors disabled:opacity-50"
            >
              {declining ? "RELEASING…" : "RELEASE MY PROFILE & STOP EMAILS"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
